import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  User,
  Info,
  ExternalLink,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import { QuartersApplicationStatus, matchStatus } from "../../../types/status";
import type { IApplication } from "../types/quarters.types";

const ApplicationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { apiRequest, isSuperAdmin } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [app, setApp] = useState<IApplication | null>(null);
  const [loading, setLoading] = useState(true);

  // Review Form State
  const [verificationStatus, setVerificationStatus] = useState<"verified" | "rejected">("verified");
  const [verificationNotes, setVerificationNotes] = useState("");
  const [remarks, setRemarks] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);

  const loadApplication = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await quartersService.getApplication(id);
      if (res.success) {
        setApp(res.data);
        setVerificationNotes(res.data.verificationNotes || "");
        setRemarks(res.data.remarks || "");
      }
    } catch (err: any) {
      toast.error("Failed to load application details");
      navigate("/quarters");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, quartersService, toast]);

  useEffect(() => {
    loadApplication();
  }, [id, loadApplication]);

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setReviewLoading(true);
    try {
      const res = await quartersService.verifyApplication(id, {
        status: verificationStatus,
        verificationNotes,
        remarks,
      });

      if (res.success) {
        toast.success(`Application verified status set to ${verificationStatus}`);
        loadApplication();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update verification status");
    } finally {
      setReviewLoading(false);
    }
  };

  // Helper to fetch S3 view url for uploaded files
  const handleViewDocument = async (key: string) => {
    try {
      const res = await apiRequest(`/uploads/view-url/${encodeURIComponent(key)}`);
      if (res.url) {
        window.open(res.url, "_blank");
      } else {
        toast.error("Unable to generate view link");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to retrieve document");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-600 font-medium">Loading details...</p>
        </div>
      </div>
    );
  }

  const isPrivileged = isSuperAdmin;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate(isPrivileged ? "/quarters/admin/applications" : "/quarters/my-applications")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Listings
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left 2 columns: Application details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main Info Card */}
          <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-6">
            <div className="flex flex-col md:flex-row justify-between md:items-center border-b border-slate-100 pb-4 gap-4">
              <div>
                <span className="bg-blue-50 text-blue-800 text-[10px] px-2.5 py-1 rounded font-bold uppercase">
                  Preferred type: {app?.quarterType}
                </span>
                <h2 className="text-xl font-bold text-slate-800 mt-2 font-serif">
                  App Number: {app?.applicationNumber || "Draft"}
                </h2>
              </div>
              <span
                className={`text-xs px-3 py-1.5 rounded-full font-bold uppercase text-center w-fit ${
                  matchStatus(app?.status, QuartersApplicationStatus.DRAFT)
                    ? "bg-slate-100 text-slate-800"
                    : matchStatus(app?.status, QuartersApplicationStatus.SUBMITTED)
                    ? "bg-blue-100 text-blue-800"
                    : matchStatus(app?.status, QuartersApplicationStatus.VERIFIED) || matchStatus(app?.status, QuartersApplicationStatus.RANKED)
                    ? "bg-indigo-100 text-indigo-800"
                    : matchStatus(app?.status, QuartersApplicationStatus.ALLOTTED)
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {app?.status}
              </span>
            </div>

            {/* Employee Bio */}
            <div>
              <h3 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-1.5">
                <User className="w-4.5 h-4.5 text-blue-600" /> Employee Profile
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-xs text-slate-600 bg-slate-50/50 p-4 border border-slate-100 rounded-2xl">
                 <div>
                   <p className="text-slate-400 font-semibold uppercase">Applicant Name</p>
                   <p className="font-bold text-slate-800 mt-0.5">
                     {typeof app?.userId === "object" ? (app.userId as { name: string }).name : "—"}
                   </p>
                 </div>
                 <div>
                   <p className="text-slate-400 font-semibold uppercase">Email Address</p>
                   <p className="font-bold text-slate-800 mt-0.5">
                     {typeof app?.userId === "object" ? (app.userId as { email: string }).email : "—"}
                   </p>
                 </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Employee ID</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.employeeId}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Department</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.department}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Designation</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.designation}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Employment Type</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.employmentType || "Permanent"}</p>
                </div>
              </div>
            </div>

            {/* Service & Seniority Info */}
            <div>
              <h3 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-1.5">
                <Clock className="w-4.5 h-4.5 text-blue-600" /> Seniority & Eligibility Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-xs text-slate-600 bg-slate-50/50 p-4 border border-slate-100 rounded-2xl">
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Service Joining Date</p>
                  <p className="font-bold text-slate-800 mt-0.5">
                    {app?.employeeDetails?.joiningDate ? new Date(app.employeeDetails.joiningDate).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Institute Seniority (Years)</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.serviceYears || 0} Years</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">PWD Disability Status</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.isPwd ? "Yes (PWD)" : "No"}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Age</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.age || 0} Years</p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Children / Dependents</p>
                  <p className="font-bold text-slate-800 mt-0.5">
                    {app?.employeeDetails?.childrenCount} Children | {app?.employeeDetails?.dependentsCount} Dependents
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Marital Status</p>
                  <p className="font-bold text-slate-800 mt-0.5">{app?.employeeDetails?.maritalStatus}</p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h3 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-1.5">
                <FileText className="w-4.5 h-4.5 text-blue-600" /> Attached Proof Documents
              </h3>
              {app?.documents && app.documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {app.documents.map((doc: any, idx: number) => (
                    <div
                      key={idx}
                      onClick={() => handleViewDocument(doc.key)}
                      className="p-4 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/20 rounded-2xl flex items-center justify-between cursor-pointer transition"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-50 text-blue-600 p-2 rounded-xl">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-slate-800 font-bold text-xs capitalize">
                            {doc.name.replace("_", " ")}
                          </p>
                          <p className="text-slate-400 text-[10px]">Click to view file</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-xs italic">No documents attached.</p>
              )}
            </div>
          </div>

          {/* Verification Actions (For verifiers and admins) */}
          {isPrivileged && matchStatus(app?.status, QuartersApplicationStatus.SUBMITTED) && (
            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
              <h3 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-1.5">
                <CheckCircle className="w-4.5 h-4.5 text-blue-600" /> Review Application
              </h3>
              <form onSubmit={handleReviewSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setVerificationStatus("verified")}
                    className={`p-3 rounded-xl border font-bold text-xs uppercase transition flex items-center justify-center gap-2 ${
                      verificationStatus === "verified"
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" /> Verify Application
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerificationStatus("rejected")}
                    className={`p-3 rounded-xl border font-bold text-xs uppercase transition flex items-center justify-center gap-2 ${
                      verificationStatus === "rejected"
                        ? "border-red-500 bg-red-50 text-red-800 shadow-sm"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <XCircle className="w-4 h-4" /> Reject Application
                  </button>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Verification Notes*</label>
                  <input
                    type="text"
                    required
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="Provide audit verification details..."
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Remarks / Review Comments</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                    rows={2}
                    placeholder="General remarks..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={reviewLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-blue-200 flex items-center gap-1"
                >
                  {reviewLoading ? "Saving Decision..." : "Submit Review Decision"}
                </button>
              </form>
            </div>
          )}

          {/* Past Review Records */}
          {app?.verifiedAt && (
            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
              <h3 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-1.5">
                <Info className="w-4.5 h-4.5 text-blue-600" /> Verification History Log
              </h3>
              <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-xl space-y-2.5 text-xs">
                <div className="flex justify-between items-center text-slate-500 font-semibold">
                  <span>Reviewed by: {typeof app.verifiedBy === "object" && app.verifiedBy
                    ? (app.verifiedBy as { name: string }).name
                    : "System Verifier"}</span>
                  <span>{new Date(app.verifiedAt).toLocaleString()}</span>
                </div>
                <p className="text-slate-700">
                  <strong>Notes:</strong> {app.verificationNotes}
                </p>
                {app.remarks && (
                  <p className="text-slate-600">
                    <strong>Remarks:</strong> {app.remarks}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Score breakdown card */}
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white border border-slate-800 p-6 rounded-3xl shadow-xl">
            <div>
              <span className="bg-white/10 text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
                Seniority Score Breakdown
              </span>
              <p className="text-[10px] text-white/50 mt-1 font-semibold">
                Approved value in housing list
              </p>
            </div>

            <div className="py-8 text-center border-b border-white/10 mb-4">
              <p className="text-6xl font-black tracking-tight text-indigo-300 font-mono">{app?.score || 0}</p>
              <p className="text-xs text-white/60 font-bold uppercase tracking-wide mt-2">Verified Score</p>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {app?.scoreBreakdown && Object.keys(app.scoreBreakdown).length > 0 ? (
                Object.entries(app.scoreBreakdown).map(([name, scoreVal]: any) => {
                  if (name === "currentRank") return null;
                  return (
                    <div key={name} className="flex justify-between items-center text-xs font-medium">
                      <span className="text-white/70">{name}</span>
                      <span className="text-indigo-300 font-bold">+{scoreVal}</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-white/40 text-xs italic text-center">No score details generated yet.</p>
              )}

              {app?.scoreBreakdown?.currentRank && (
                <div className="flex justify-between items-center text-xs font-black border-t border-white/10 pt-3 mt-3 text-emerald-400">
                  <span>Current Roster Rank</span>
                  <span>#{app.scoreBreakdown.currentRank}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail;
